import React, { useEffect, useState } from 'react';
import { X, History, MapPin, ArrowRight, User, Calendar, FileText } from 'lucide-react';
import api from './axiosConfig';
import { useTranslation } from 'react-i18next';

const MoveHistoryModal = ({ isOpen, onClose, assetNum }) => {
    const { t, i18n } = useTranslation();
    const isRTL = i18n.language === 'ar' || i18n.language === 'ur';
    const [history, setHistory] = useState([]);
    const [loading, setLoading] = useState(true);

    useEffect(() => {
        if (isOpen && assetNum) {
            const fetchHistory = async () => {
                setLoading(true);
                try {
                    const res = await api.get(`/api/assets/move/history/${assetNum}`);
                    setHistory(res.data);
                } catch (err) {
                    console.error("Failed to fetch move history");
                } finally {
                    setLoading(false);
                }
            };
            fetchHistory();
        }
    }, [isOpen, assetNum]);

    if (!isOpen) return null;

    return (
        <div className="fixed inset-0 z-[10006] flex items-center justify-center bg-black/60 backdrop-blur-sm p-4 animate-in fade-in duration-200" dir={isRTL ? 'rtl' : 'ltr'}>
            <div className="bg-surface-card w-full max-w-5xl shadow-2xl rounded-sm border border-line flex flex-col max-h-[85vh]">

                {/* Header - Maximo Style */}
                <div className="bg-primary-dark text-ink-inverse p-3 flex justify-between items-center border-b-2 border-primary">
                    <div className="flex items-center gap-3">
                        <History size={20} className="text-ink-inverse/70" />
                        <div>
                            <h3 className="font-black text-xs uppercase tracking-widest">{t('moveHistoryModal.title')}</h3>
                            <p className="text-[10px] text-ink-inverse/70 font-bold uppercase">{t('moveHistoryModal.auditTrail', { assetNum })}</p>
                        </div>
                    </div>
                    <button onClick={onClose} className="hover:bg-ink-inverse/10 p-1 rounded transition-colors"><X size={20}/></button>
                </div>

                {/* Content */}
                <div className="flex-1 overflow-y-auto p-6 bg-surface-panel">
                    {loading ? (
                        <div className="flex justify-center p-20 font-black text-ink-secondary animate-pulse uppercase tracking-widest italic">{t('moveHistoryModal.fetching')}</div>
                    ) : history.length === 0 ? (
                        <div className="text-center p-20 text-ink-secondary italic font-bold">{t('moveHistoryModal.noRecords')}</div>
                    ) : (
                        <div className="overflow-x-auto border bg-surface-card shadow-md rounded-sm">
                            <table className={`w-full text-[11px] border-collapse ${isRTL ? 'text-right' : 'text-left'}`}>
                                <thead className="bg-surface-panel border-b-2 border-line text-primary font-black uppercase">
                                    <tr>
                                        <th className="p-3 border-r">{t('moveHistoryModal.thDate')}</th>
                                        <th className="p-3 border-r">{t('moveHistoryModal.thFrom')}</th>
                                        <th className="p-3 border-r text-center w-10"></th>
                                        <th className="p-3 border-r">{t('moveHistoryModal.thTo')}</th>
                                        <th className="p-3 border-r">{t('moveHistoryModal.thReason')}</th>
                                        <th className="p-3">{t('moveHistoryModal.thMovedBy')}</th>
                                    </tr>
                                </thead>
                                <tbody className="divide-y divide-line-subtle font-bold">
                                    {history.map((item, idx) => (
                                        <tr key={idx} className="hover:bg-primary-soft/50 transition-colors group">
                                            <td className="p-3 border-r text-ink-secondary whitespace-nowrap">
                                                <div className="flex items-center gap-2">
                                                    <Calendar size={12} className="text-ink-secondary" />
                                                    {new Date(item.movedate).toLocaleString('sv-SE').slice(0, 16)}
                                                </div>
                                            </td>

                                            <td className="p-3 border-r bg-surface-panel/50">
                                                <div className="flex flex-col gap-1">
                                                    <span className="text-ink-secondary line-through decoration-status-danger/40">{item.from_location || t('moveHistoryModal.empty')}</span>
                                                    <span className="text-[9px] text-ink-secondary italic font-normal">{t('moveHistoryModal.parent', { name: item.from_parent || t('moveHistoryModal.none') })}</span>
                                                </div>
                                            </td>

                                            <td className="p-3 border-r text-center">
                                                <ArrowRight size={14} className={`text-primary mx-auto group-hover:translate-x-1 transition-transform ${isRTL ? 'rotate-180 group-hover:-translate-x-1' : ''}`} />
                                            </td>

                                            <td className="p-3 border-r">
                                                <div className="flex flex-col gap-1">
                                                    <span className="text-primary font-black">{item.to_location || t('moveHistoryModal.empty')}</span>
                                                    <span className="text-[9px] text-primary/60 italic font-black uppercase">{t('moveHistoryModal.parent', { name: item.to_parent || t('moveHistoryModal.none') })}</span>
                                                </div>
                                            </td>

                                            <td className="p-3 border-r text-ink-secondary font-normal italic max-w-xs truncate">
                                                <div className="flex items-start gap-2">
                                                    <FileText size={12} className="text-ink-secondary/40 mt-0.5" />
                                                    {item.memo}
                                                </div>
                                            </td>

                                            <td className="p-3 text-ink text-[10px] uppercase">
                                                <div className="flex items-center gap-2">
                                                    <User size={12} className="text-ink-secondary" />
                                                    {item.moveby}
                                                </div>
                                            </td>
                                        </tr>
                                    ))}
                                </tbody>
                            </table>
                        </div>
                    )}
                </div>

                {/* Footer */}
                <div className="bg-surface-panel p-4 border-t flex justify-end">
                    <button
                        onClick={onClose}
                        className="px-8 py-2 bg-primary-dark text-ink-inverse font-black text-[10px] uppercase shadow-lg hover:bg-primary transition-all"
                    >
                        {t('moveHistoryModal.closeHistory')}
                    </button>
                </div>
            </div>
        </div>
    );
};

export default MoveHistoryModal;
